// components/AuthWrapper.js
// UPDATED: 2026-08-20 — added logging around the LOGIN_SUCCESS/LOGOUT dispatch
// this component sends on mount, so it's traceable alongside the new
// [AuthReducer] logs and AdminLogin.js's [AdminLogin][Redux] logs. This is
// the ONLY place auth state gets set from a server check on a normal page
// load — worth knowing exactly what it dispatches and when.
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { checkSession } from '../actions/auth';
import LoadingSpinner from './LoadingSpinner';

const AuthWrapper = ({ children }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      console.log('[AuthWrapper] Mounting — running startup checkSession()');
      dispatch({ type: 'AUTH_LOADING' });

      try {
        const authResult = await checkSession();

        console.log('[AuthWrapper] Startup session check:', authResult);

        // ⭐ BUG FIX: checkSession() (actions/auth.js) returns a FLAT object —
        // { isAuthenticated, hasSubscribed, sessionId, author, email,
        //   accountType, systemRole, ... } — there is no nested .user
        // property. The condition here used to check `authResult.user`,
        // which was always undefined, so this branch NEVER ran and every
        // page load/refresh silently fell through to the 'not authenticated'
        // branch below and dispatched LOGOUT — even with a fully valid
        // session. Confirmed live: checkSession() returned
        // isAuthenticated:true and AuthWrapper still logged 'No
        // authenticated session on mount' immediately after.
        // Pre-existing bug, not introduced this session — just never visibly
        // surfaced because PrivateRoute.js does its own independent fetch
        // (reading response.data.user from the RAW backend response, not
        // through checkSession()'s transformed shape) and AdminLogin.js's
        // post-login dispatch bypasses checkSession()/AuthWrapper entirely.
        // Only the shared nav, which depends solely on this dispatch, was
        // actually broken.
        if (authResult && authResult.isAuthenticated) {
          const user = authResult; // already flat — use directly, no .user unwrap

          const isAdmin = user.accountType === 'Admin';
          const hasSubscribed = user.hasSubscribed || user.accountType === 'Subscriber';

          const loginPayload = {
            ...user,
            isAuthenticated: true,
            hasSubscribed,
            accountType: user.accountType || 'Subscriber',
          };

          console.log('[AuthWrapper] Dispatching LOGIN_SUCCESS — isAdmin:', isAdmin,
            'systemRole:', user.systemRole ?? '(none)');
          console.log('[AuthWrapper]   payload →', loginPayload);

          // Store session data in Redux
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: loginPayload,
          });

          // Sync chatbot context
          dispatch({
            type: 'RESET_CHATBOT',
            payload: {
              isAuthenticated: true,
              hasSubscribed
            }
          });
        } else {
          console.log('[AuthWrapper] No authenticated session on mount — dispatching LOGOUT');
          dispatch({ type: 'LOGOUT' });

          dispatch({
            type: 'RESET_CHATBOT',
            payload: {
              isAuthenticated: false,
              hasSubscribed: false
            }
          });
        }
      } catch (error) {
        console.error('[AuthWrapper] Session check error:', error);
        dispatch({ type: 'LOGIN_FAILURE', payload: error.message });
      } finally {
        // Slight delay for smooth UI
        setTimeout(() => {
          setLoading(false);
        }, 300);
      }
    };

    initAuth();
  }, [dispatch]);

  if (loading) {
    return <LoadingSpinner />;
  }

  return <>{children}</>;
};

export default AuthWrapper;